import { Link } from 'react-router-dom';
import { FiArrowRight, FiTag } from 'react-icons/fi';
import { FaGlobeAsia, FaPlane } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import preorderBackground from '../../assets/Pre-Order.png';
import './DeliveryAnnouncement.css';

const formatAmount = (value) => Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const DeliveryGlobe = () => (
  <span
    className="delivery-announcement__globe"
    aria-hidden="true"
  >
    <FaGlobeAsia className="delivery-announcement__globe-icon" focusable="false" />
    <FaPlane className="delivery-announcement__plane-icon" focusable="false" />
  </span>
);

const couponMessage = (coupon) => {
  if (coupon.tickerMessage?.trim()) return coupon.tickerMessage.trim();
  const saving = coupon.type === 'percent'
    ? `${formatAmount(coupon.value)}% OFF`
    : `₹${formatAmount(coupon.value)} OFF`;
  const minimum = Number(coupon.minOrder) > 0
    ? ` on orders above ₹${formatAmount(coupon.minOrder)}`
    : '';
  const maximum = coupon.type === 'percent' && Number(coupon.maxDiscount) > 0
    ? ` (up to ₹${formatAmount(coupon.maxDiscount)})`
    : '';
  return `Use ${coupon.code} — ${saving}${minimum}${maximum}`;
};

const DeliveryAnnouncement = ({ coupons = [], settings }) => {
  const { addToast } = useToast();

  const copyCoupon = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      addToast(`Coupon ${code} copied`);
    } catch {
      addToast(`Enter ${code} at checkout to use this offer`, 'info');
    }
  };

  return (
    <aside
      className="delivery-announcement"
      style={{ backgroundImage: `url(${preorderBackground})` }}
      aria-label="Delivery, offers and pre-orders"
    >
      <Link
        to="/shipping"
        className="delivery-announcement__brand"
      >
        <DeliveryGlobe />
        <span className="delivery-announcement__brand-copy">
          <span className="delivery-announcement__heading">
            Worldwide <strong>Delivery</strong>
          </span>
          <span className="delivery-announcement__tagline">Bringing Indian culture to your doorstep</span>
        </span>
      </Link>

      <div className="delivery-announcement__offers">
        {coupons.length > 0 && (
          <div
            className="delivery-announcement__coupon-marquee"
            role="region"
            aria-label="Available coupons; select an offer to copy its code"
          >
            <div
              className="delivery-announcement__coupon-track"
              style={{ '--coupon-scroll-duration': `${Math.max(18, coupons.length * 12)}s` }}
            >
              {[false, true].map((duplicate) => (
                <div
                  key={String(duplicate)}
                  className="delivery-announcement__coupon-group"
                  aria-hidden={duplicate ? true : undefined}
                >
                  {coupons.map((coupon) => (
                    <button
                      key={coupon.id || coupon.code}
                      type="button"
                      className="delivery-announcement__offer"
                      tabIndex={duplicate ? -1 : undefined}
                      onClick={() => copyCoupon(coupon.code)}
                      aria-label={`${couponMessage(coupon)}. Copy code ${coupon.code}`}
                      title={`Copy ${coupon.code}`}
                    >
                      <FiTag aria-hidden="true" />
                      <span>{couponMessage(coupon)}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="delivery-announcement__preorder">
        <p>
          {settings?.preorderTickerText || 'Coming Soon To Your Bookshelf...'}
        </p>
        <Link
          to="/shop?preorder=true"
          className="delivery-announcement__button"
        >
          {settings?.preorderButtonLabel || 'Books On Pre-Order'}
          <FiArrowRight aria-hidden="true" />
        </Link>
      </div>
    </aside>
  );
};

export default DeliveryAnnouncement;
